import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Presentation } from 'lucide-react';
import { Card } from '@/components/common/Card';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States';
import { PageHeader } from '@/components/layout/PageHeader';
import { ROUTES } from '@/app/router/routes';
import { useAuth } from '@/features/auth/useAuth';
import {
  SectionTitle,
  WeekGrid,
  entriesForDay,
  localToday,
  todayDay,
} from '@/features/portal/shared';
import { timetablesApi } from '@/services/api/endpoints/teaching';
import { queryKeys } from '@/services/api/queryKeys';
import { PERMISSIONS } from '@/types/permissions';

/**
 * A teacher's home: their published week across every section, today's lessons,
 * and a register to take for each class they see today.
 *
 * The week comes from GET /timetables/me, resolved from the signed-in account —
 * an empty list for somebody who is not a teacher, rather than an error.
 */
export function MyTeachingPage() {
  const { can } = useAuth();
  const mayTakeRegister = can(PERMISSIONS.STUDENT_ATTENDANCE_VIEW);

  const week = useQuery({
    queryKey: queryKeys.timetables.mine,
    queryFn: timetablesApi.mine,
  });

  const header = (
    <PageHeader
      title="My teaching"
      description="Your published week, and today’s classes."
      icon={<Presentation className="h-5 w-5" aria-hidden="true" />}
    />
  );

  if (week.isPending) {
    return (
      <div>
        {header}
        <Card>
          <LoadingState label="Loading your timetable" />
        </Card>
      </div>
    );
  }
  if (week.isError) {
    return (
      <div>
        {header}
        <Card>
          <ErrorState error={week.error} onRetry={() => void week.refetch()} />
        </Card>
      </div>
    );
  }
  if (week.data.length === 0) {
    return (
      <div>
        {header}
        <Card>
          <EmptyState
            icon={<Presentation className="h-6 w-6" aria-hidden="true" />}
            title="No lessons on a published timetable"
            description="Once a timetable with your lessons is published, your week appears here."
          />
        </Card>
      </div>
    );
  }

  const date = localToday();
  const today = entriesForDay(week.data, todayDay());
  const sections = [
    ...new Map(
      today
        .filter((entry) => entry.sectionId !== undefined)
        .map((entry) => [entry.sectionId as string, entry.sectionName ?? 'Class']),
    ).entries(),
  ];

  return (
    <div className="space-y-4">
      {header}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card>
          <SectionTitle>Today’s lessons</SectionTitle>
          {today.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted">No lessons today.</p>
          ) : (
            <ul className="divide-y divide-line">
              {today.map((entry) => (
                <li key={entry.id} className="grid grid-cols-[88px_minmax(0,1fr)] gap-3 px-4 py-2.5">
                  <span className="text-xs font-semibold text-muted">{entry.periodName ?? 'Period'}</span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-ink">
                      {entry.subjectName ?? '—'}
                    </span>
                    <span className="block truncate text-xs text-muted">
                      {[entry.sectionName, entry.room].filter(Boolean).join(' · ') || '—'}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <SectionTitle>Registers for today</SectionTitle>
          {sections.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted">No classes today.</p>
          ) : !mayTakeRegister ? (
            <p className="px-4 py-6 text-sm text-muted">
              Taking a register needs attendance access. Ask an administrator.
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {sections.map(([sectionId, sectionName]) => (
                <li key={sectionId}>
                  <Link
                    to={`${ROUTES.attendance}?sectionId=${sectionId}&date=${date}`}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-ink transition-colors hover:bg-surface-2"
                  >
                    <ClipboardList className="h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
                    <span className="min-w-0 flex-1 truncate">{sectionName}</span>
                    <span className="text-xs font-semibold text-accent">Take register</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card>
        <SectionTitle>My week</SectionTitle>
        <WeekGrid
          entries={week.data}
          renderCell={(entry) => (
            <span className="block">
              <span className="block text-sm font-semibold text-ink">{entry.subjectName ?? '—'}</span>
              <span className="block text-[11px] text-muted">
                {[entry.sectionName, entry.room].filter(Boolean).join(' · ')}
              </span>
            </span>
          )}
        />
      </Card>
    </div>
  );
}
